import { useState, useCallback } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';

/**
 * useConfirm Hook
 * Provides a way to trigger a confirmation dialog imperatively via a promise.
 * 
 * Usage:
 * const { confirm, ConfirmDialog } = useConfirm();
 * const handleDelete = async () => {
 *   const isConfirmed = await confirm({ title: 'Delete Item', message: 'Are you sure?' });
 *   if (isConfirmed) { ... }
 * };
 * 
 * Render <ConfirmDialog /> in your JSX.
 */
export const useConfirm = () => {
    const [dialogConfig, setDialogConfig] = useState(null);
    const [resolveRef, setResolveRef] = useState(null);

    const confirm = useCallback(({ title, message, confirmText, cancelText, variant = 'danger' }) => {
        return new Promise((resolve) => {
            setDialogConfig({
                title,
                message,
                confirmText,
                cancelText,
                variant
            });
            setResolveRef(() => resolve);
        });
    }, []);

    const handleConfirm = useCallback(() => {
        if (resolveRef) {
            resolveRef(true);
        }
        setDialogConfig(null);
    }, [resolveRef]);

    const handleCancel = useCallback(() => {
        if (resolveRef) {
            resolveRef(false);
        }
        setDialogConfig(null);
    }, [resolveRef]);

    const DialogComponent = () => (
        <ConfirmDialog
            isOpen={!!dialogConfig}
            title={dialogConfig?.title}
            message={dialogConfig?.message}
            confirmText={dialogConfig?.confirmText}
            cancelText={dialogConfig?.cancelText}
            variant={dialogConfig?.variant}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
        />
    );

    return {
        confirm,
        ConfirmDialog: DialogComponent
    };
};

export default useConfirm;
